import { Sparkles, MessagesSquare, CalendarCheck, TrendingUp, Check } from "lucide-react";

const FEATURES = [
  {
    icon: MessagesSquare,
    title: "AI answers your enquiries",
    body: "Our assistant replies to tenant questions on your listing 24/7 — from the details you provide — so you never miss a lead while you're busy.",
  },
  {
    icon: CalendarCheck,
    title: "Visits booked for you",
    body: "Set your visiting hours once. Tenants pick a slot and you get an instant WhatsApp + email alert — just confirm or decline.",
  },
  {
    icon: TrendingUp,
    title: "Seen more on Google",
    body: "AI cleans up your title & description for search, so more renters actually find your property.",
  },
];

/**
 * Landlord-facing intro shown above the listing form. A trust / differentiation
 * section — the AI help is included with every listing (not a paid upsell).
 * Mobile-first.
 */
export default function LandlordAiIntro() {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-8" data-testid="landlord-ai-intro">
      <div className="rounded-3xl bg-gradient-to-br from-[#7A1E2A] to-[#5C1420] text-white p-6 sm:p-9 relative overflow-hidden">
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#C9A24A]/20 blur-2xl" />
        <div className="relative">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#C9A24A]/20 border border-[#C9A24A]/40 text-[#F3E4BC] text-[10px] font-bold tracking-widest uppercase mb-3">
            <Sparkles size={12} /> Included with every listing
          </div>
          <h2 className="font-display text-2xl sm:text-4xl leading-tight">
            List once. Let AI bring you tenants.
          </h2>
          <p className="text-white/85 text-sm sm:text-base mt-3 max-w-2xl">
            You're busy — so our AI does the chasing. It answers renters, books your visits and puts your listing in front of more people. You just approve the ones you like, always with zero brokerage.
          </p>
        </div>
      </div>

      {/* Benefit blocks — given visual weight so this reads as a reason to choose us */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-5">
        {FEATURES.map((f) => (
          <div
            key={f.title}
            className="group rounded-2xl border border-brand-subtle bg-white p-5 relative overflow-hidden hover:border-[#C9A24A] hover:shadow-[0_16px_40px_-20px_rgba(122,30,42,0.35)] transition-all"
          >
            <span className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#C9A24A] to-[#E5C97B]" />
            <div className="w-12 h-12 rounded-xl bg-[#FBF6EC] flex items-center justify-center mb-3 group-hover:scale-105 transition-transform">
              <f.icon size={22} className="text-[#C9A24A]" />
            </div>
            <h3 className="font-display text-lg brand-forest mb-1.5">{f.title}</h3>
            <p className="text-sm text-gray-500 leading-relaxed">{f.body}</p>
          </div>
        ))}
      </div>

      <p className="flex items-center justify-center gap-1.5 text-center text-xs text-gray-500 mt-5">
        <Check size={13} className="text-emerald-600" />
        All three work automatically the moment your listing goes live — no setup, no extra steps.
      </p>
    </div>
  );
}
